import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * SidePanel — a details panel that slides over the right edge of the page
 * while the page stays visible behind a light scrim.
 *
 * Sits above the app header (z 2000) and below AppModal dialogs (z 2100),
 * so a form opened from the panel appears on top of it. Escape closes the
 * panel unless a dialog is open over it; so does a click on the scrim.
 *
 * Props: open, onClose, title, subtitle?, children, footer?
 */
export const SidePanel = ({ open, onClose, title, subtitle, children, footer }) => {
  const panelRef = useRef(null);
  const titleId = useId();
  // Read through a ref so a new onClose on each parent render does not re-run
  // the effect below, which would pull focus out of a form opened over the panel.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    panelRef.current?.focus();
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      const dialogAbove = Array.from(document.querySelectorAll('[aria-modal="true"]'))
        .some((element) => element !== panelRef.current);
      if (dialogAbove) return;
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 2000 }}>
      <div className="absolute inset-0 bg-black/20" onClick={onClose} aria-hidden="true" />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-[440px] bg-white flex flex-col outline-none"
        style={{ boxShadow: '-12px 0 40px rgba(0,0,0,0.12)' }}
      >
        <header className="px-6 pt-5 pb-4 border-b border-black/[0.06]">
          <div className="flex items-start justify-between gap-3">
            <h2 id={titleId} className="m-0 text-[18px] font-semibold text-lb-ink-strong leading-[1.3]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close details"
              className="p-1 -mr-1 rounded-md bg-transparent border-0 text-lb-muted hover:bg-black/[0.05] focus-ring"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          {subtitle && <div className="mt-1">{subtitle}</div>}
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer className="px-6 py-4 border-t border-black/[0.06] flex items-center justify-between gap-2">
            {footer}
          </footer>
        )}
      </aside>
    </div>,
    document.body
  );
};
